import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const FirstLoginPasswordChange = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 4) {
      toast.error('Passwort muss mindestens 4 Zeichen haben');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/auth/change-password-first-login`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Passwort erfolgreich geändert!');
      
      // Refresh user state from backend to get updated must_change_password flag
      await refreshUser();
      
      // Navigate to employee dashboard
      setTimeout(() => {
        navigate('/employee/dashboard');
      }, 500);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.detail?.errors) {
        error.response.data.detail.errors.forEach(err => toast.error(err));
      } else {
        toast.error('Fehler beim Ändern des Passworts');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
            <Lock className="h-6 w-6" />
            Passwort ändern
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-900 font-semibold mb-2">
              ⚠️ Erstmaliger Login
            </p>
            <p className="text-sm text-amber-800">
              Bitte ändern Sie Ihr Passwort, bevor Sie fortfahren. Ihr Administrator hat ein temporäres Passwort gesetzt.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new_password">Neues Passwort (min. 4 Zeichen)</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Neues Passwort"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm_password">Passwort wiederholen</Label>
              <Input
                id="confirm_password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Wird geändert...' : 'Passwort ändern'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FirstLoginPasswordChange;
